<template>
  <div>
    <h5 class="text-center mb-2">Top 10 IPs</h5>
    <table class="table table-striped table-primary table-sm table-bordered">
      <thead>
      <tr>
        <th>IP</th>
        <th>Visits</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(item, idx) in topIps" :key="idx">
        <td>{{ item.ip }}</td>
        <td>{{ item.visits }}</td>
      </tr>
      </tbody>
    </table>
    <div v-if="loading">Loading...</div>
    <div v-if="error" class="text-danger">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';

const topIps = ref([]);
const loading = ref(false);
const error = ref(null);

onMounted(/**
 * Fetches and processes data about visited links from an API endpoint.
 *
 * The function retrieves the list of visited links and aggregates the count of visits
 * for each unique IP address. It then sorts the IP addresses by the number of visits
 * in descending order and selects the top 10 most visited IPs. This data is stored in `topIps`.
 * In case of an error, an error message is set in the `error` reactive value.
 * During the execution, the `loading` reactive variable indicates the loading state.
 *
 * Data Flow:
 * 1. Retrieve the API URL from the environment variable `VUE_APP_API_URL`.
 * 2. Retrieve the user token from `sessionStorage`.
 * 3. Make an authenticated GET request to fetch visited links data.
 * 4. Process the data to calculate visit counts for each IP address.
 * 5. Sort and truncate to retain the top 10 IP addresses with highest visits.
 *
 * Reactive Variables:
 * - `loading`: Set to `true` during the API call and `false` after completion.
 * - `topIps`: Stores the top 10 IPs with their corresponding visit counts.
 * - `error`: Stores an error message in case the API request fails.
 *
 * External Dependencies:
 * - `axios`: Used to make the GET request to the API.
 * - `process.env.VUE_APP_API_URL`: Environment variable for the API base URL.
 * - `sessionStorage`: Used to retrieve the authentication token.
 *
 * Error Handling:
 * Sets the `error` variable to 'Failed to load IP data' if the API call fails or if the data processing fails.
 *
 * Permissions:
 * Requires a valid user token to be present in the session storage for API authentication.
 *
 * Limitations:
 * Assumes that the `linksVisited` array is returned in the expected format from the API.
 */
async () => {
  loading.value = true;
  const apiUrl = process.env.VUE_APP_API_URL;
  const token = sessionStorage.getItem('token');
  try {
    const res = await axios.get(`${apiUrl}/linksvisited`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res && Array.isArray(res.data.linksVisited)) {
      const counts = {};
      res.data.linksVisited.forEach(item => {
        const ip = item?.ip || 'Unknown';
        counts[ip] = (counts[ip] || 0) + 1;
      });
      topIps.value = Object.entries(counts)
          .map(([ip, visits]) => ({ ip, visits }))
          .sort((a, b) => b.visits - a.visits)
          .slice(0, 10);
    }
  } catch (e) {
    error.value = 'Failed to load IP data';
  } finally {
    loading.value = false;
  }
});
</script>
